/**
 * Pluralizes a word based on the given value
 * @param value - The numeric value to determine if pluralization is needed
 * @param word - The word to pluralize
 * @returns The pluralized word if value > 1, otherwise the original word
 */
export function pluralizeWord(value: number, word: string): string {
  // If value is 1 or less, return the original word
  if (value <= 1) {
    return word;
  }

  // Get the last character of the word
  const lastChar = word.slice(-1).toLowerCase();
  const lastTwoChars = word.slice(-2).toLowerCase();

  // Words ending in 's', 'x', 'z', 'ch', 'sh' get 'es'
  if (
    lastChar === "s" ||
    lastChar === "x" ||
    lastChar === "z" ||
    lastTwoChars === "ch" ||
    lastTwoChars === "sh"
  ) {
    return word + "es";
  }

  // Words ending in 'y' preceded by a consonant get 'ies'
  if (lastChar === "y") {
    const secondLastChar = word.slice(-2, -1).toLowerCase();
    const consonants = "bcdfghjklmnpqrstvwxz";
    if (consonants.includes(secondLastChar)) {
      return word.slice(0, -1) + "ies";
    }
    // If 'y' is preceded by a vowel, just add 's'
    return word + "s";
  }

  // Words ending in 'f' or 'fe' get 'ves' (common cases)
  if (lastChar === "f") {
    return word.slice(0, -1) + "ves";
  }
  if (lastTwoChars === "fe") {
    return word.slice(0, -2) + "ves";
  }

  // Words ending in 'o' preceded by a consonant get 'es'
  if (lastChar === "o") {
    const secondLastChar = word.slice(-2, -1).toLowerCase();
    const consonants = "bcdfghjklmnpqrstvwxz";
    if (consonants.includes(secondLastChar)) {
      return word + "es";
    }
    // If 'o' is preceded by a vowel, just add 's'
    return word + "s";
  }

  // Default case: just add 's'
  return word + "s";
}
